import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, MapPin, Hand, MessageCircle, Check } from "lucide-react";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import {
  presenceHeartbeat,
  presenceClear,
  listNearbyAtEvent,
  waveAt,
} from "@/lib/nearby";

/**
 * NearbyPanel — beta "Nearby at this event" surface for the event detail
 * page. Renders ONLY when:
 *   • feature flag is on (env var)
 *   • user has opted in (profile.nearby_beta_opt_in)
 *   • the host has coords on the event
 *   • the viewer has RSVP'd (parent gates this)
 *
 * UX rules (V1):
 *   • Explicit opt-in every event — no auto-enable.
 *   • Floating randomised avatar grid. No coordinates, no distance, no map.
 *   • 25s heartbeat while opted-in and tab visible.
 *   • Auto-disables if the user drifts >100m from the event (server tells us).
 *   • One-tap "Wave 👋" per avatar; mutual wave deep-links into existing DMs.
 *   • Pure foreground tracking. Pausing/unmounting/tab-hiding clears presence.
 */
export default function NearbyPanel({ event }) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const featureFlagOn = process.env.REACT_APP_NEARBY_BETA === "1";
  const userOptedInBeta = !!profile?.nearby_beta_opt_in;
  const eventHasCoords =
    event?.latitude != null && event?.longitude != null;

  // Per-event explicit opt-in state. Lives in sessionStorage so refreshing
  // mid-event keeps you visible, but the next session starts opted-out
  // again (matches "explicit opt-in every event").
  const sessKey = `inonit:nearby-on:${event?.id || "none"}`;
  const [active, setActive] = useState(() => {
    try { return sessionStorage.getItem(sessKey) === "1"; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false); // last heartbeat said in-range
  const [people, setPeople] = useState([]);
  const [waving, setWaving] = useState(() => new Set());
  const stopRef = useRef(null);

  // Quick early-exits so the panel is purely additive — never renders for
  // anyone outside the beta cohort.
  const shouldRender = featureFlagOn && userOptedInBeta && eventHasCoords;

  // Drive heartbeat + polling while `active`.
  useEffect(() => {
    if (!active || !shouldRender || !user?.id) return undefined;

    let cancelled = false;
    let intervalId = null;
    let lastBoundCoord = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, {
            enableHighAccuracy: true,
            timeout: 8000,
            maximumAge: 15000,
          })
        );
        lastBoundCoord = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const v = await presenceHeartbeat(event.id, lastBoundCoord);
        if (cancelled) return;
        setVisible(v);
        // Pull the nearby roster on the same cadence.
        const rows = await listNearbyAtEvent(event.id);
        if (!cancelled) setPeople(rows);
      } catch (err) {
        // GPS / RPC error — keep the UI alive, just don't update the roster.
        if (err?.code === 1 /* PERMISSION_DENIED */) {
          toast.error("Location access denied — turning Nearby off.");
          stopActive();
        }
      }
    };

    // First tick now, then every 25s.
    tick();
    intervalId = setInterval(tick, 25000);

    // Visibility / pageshow cleanup. If the tab is hidden for >60s, clear
    // presence so the user disappears server-side.
    let hiddenSince = null;
    const onVis = async () => {
      if (document.hidden) {
        hiddenSince = Date.now();
      } else if (hiddenSince && Date.now() - hiddenSince > 60_000) {
        // Resumed after >1 min — re-heartbeat immediately.
        hiddenSince = null;
        tick();
      } else {
        hiddenSince = null;
      }
    };
    document.addEventListener("visibilitychange", onVis);

    // beforeunload — best-effort clear so we don't leak presence.
    const onUnload = () => {
      try { presenceClear(event.id); } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", onUnload);

    stopRef.current = async () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", onUnload);
      try { await presenceClear(event.id); } catch { /* ignore */ }
    };

    return () => { stopRef.current?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, shouldRender, user?.id, event?.id]);

  const stopActive = () => {
    try { sessionStorage.removeItem(sessKey); } catch { /* ignore */ }
    setActive(false);
    setVisible(false);
    setPeople([]);
  };

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (active) {
        await presenceClear(event.id);
        stopActive();
        toast.message("Nearby turned off for this event.");
      } else {
        if (!navigator.geolocation) {
          toast.error("Location not available on this device.");
          return;
        }
        try { sessionStorage.setItem(sessKey, "1"); } catch { /* ignore */ }
        setActive(true);
        toast.success("You're nearby — say hi to the green dots!");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleWave = async (target) => {
    if (waving.has(target.user_id)) return;
    setWaving((s) => new Set(s).add(target.user_id));
    try {
      // Optimistic: mark i_waved immediately so the button reflects.
      setPeople((rows) =>
        rows.map((r) =>
          r.user_id === target.user_id ? { ...r, i_waved: true } : r
        )
      );
      const { mutual } = await waveAt(event.id, target.user_id);
      if (mutual) {
        setPeople((rows) =>
          rows.map((r) =>
            r.user_id === target.user_id ? { ...r, mutual: true, they_waved: true } : r
          )
        );
        toast.success(`You both waved 👋 — saying hi to ${target.display_name || target.username || "them"}.`);
        navigate(`/messages/${target.user_id}`);
      } else {
        toast.message("Wave sent.");
      }
    } catch {
      // Rollback.
      setPeople((rows) =>
        rows.map((r) =>
          r.user_id === target.user_id ? { ...r, i_waved: false } : r
        )
      );
      toast.error("Couldn't send wave.");
    } finally {
      setWaving((s) => { const n = new Set(s); n.delete(target.user_id); return n; });
    }
  };

  // Deterministic small offsets per user-id so avatars float at varied
  // positions but don't reshuffle every render. Hash → -20..20 px.
  const layout = useMemo(() => {
    const hash = (s) => {
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
      return h;
    };
    return new Map(
      people.map((p) => {
        const h = hash(p.user_id || "");
        return [p.user_id, {
          dx: ((h & 0xff) % 40) - 20,
          dy: (((h >> 8) & 0xff) % 40) - 20,
        }];
      })
    );
  }, [people]);

  if (!shouldRender) return null;

  return (
    <section
      data-testid="nearby-panel"
      className="mt-8 border-[2.5px] border-ink rounded-3xl bg-paper shadow-pop-sm overflow-hidden"
    >
      <header className="flex items-center justify-between px-5 py-4 border-b-[2.5px] border-ink bg-lime/30">
        <div className="flex items-center gap-2">
          <Sparkles size={16} strokeWidth={2.6} className="text-ink" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-extrabold">
            Nearby · Beta
          </span>
        </div>
        {active && (
          <span
            data-testid="nearby-status"
            className={
              "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-extrabold " +
              (visible ? "text-lime-700" : "text-ink/55")
            }
          >
            <span className={"w-2 h-2 rounded-full " + (visible ? "bg-lime-500 animate-pulse" : "bg-ink/30")} />
            {visible ? "You're visible" : "Walk closer to be seen"}
          </span>
        )}
      </header>

      <div className="px-5 py-5">
        {!active ? (
          <OptInState busy={busy} onToggle={handleToggle} />
        ) : (
          <ActiveState
            people={people}
            layout={layout}
            visible={visible}
            waving={waving}
            onWave={handleWave}
            onTurnOff={handleToggle}
          />
        )}
      </div>
    </section>
  );
}

function OptInState({ busy, onToggle }) {
  return (
    <div className="flex flex-col items-start gap-4">
      <h3 className="font-display text-2xl font-bold tracking-tight leading-tight">
        Find your people<br />at this event.
      </h3>
      <p className="text-sm text-ink/70 leading-relaxed">
        Turn on Nearby to see other attendees within ~100m of the venue.
        No map, no distance, no location shared — just floating avatars and
        a one-tap wave. Disappears when you leave.
      </p>
      <ul className="text-[12px] text-ink/55 leading-relaxed space-y-1.5">
        <li className="flex items-center gap-2">
          <MapPin size={12} strokeWidth={2.6} /> Only works inside the event radius.
        </li>
        <li className="flex items-center gap-2">
          <Hand size={12} strokeWidth={2.6} /> Both must wave to unlock chat.
        </li>
        <li className="flex items-center gap-2">
          <Check size={12} strokeWidth={2.6} /> Disappears the moment you leave.
        </li>
      </ul>
      <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        data-testid="nearby-opt-in"
        className="mt-2 bg-ink text-paper border-[2.5px] border-ink rounded-full px-6 py-3 font-extrabold uppercase tracking-widest text-xs hover:bg-paper hover:text-ink transition-colors inline-flex items-center gap-2 disabled:opacity-60"
      >
        {busy && <Loader2 size={14} className="animate-spin" />}
        Turn on Nearby
      </button>
    </div>
  );
}

function ActiveState({ people, layout, visible, waving, onWave, onTurnOff }) {
  return (
    <>
      <h3 className="font-display text-2xl font-bold tracking-tight leading-tight mb-1">
        People around you<br />right now.
      </h3>
      <p className="text-sm text-ink/55 mb-5">
        {visible
          ? people.length === 0
            ? "No one else nearby yet. Hang tight, this updates every ~25s."
            : `${people.length} ${people.length === 1 ? "person" : "people"} within range.`
          : "Step closer to the venue — you're just outside the radius."}
      </p>

      {visible && people.length > 0 && (
        <div
          data-testid="nearby-avatars"
          className="relative bg-paper border-[2px] border-dashed border-ink/25 rounded-2xl min-h-[220px] p-4 flex flex-wrap justify-center items-center gap-x-4 gap-y-5"
        >
          {people.map((p) => {
            const off = layout.get(p.user_id) || { dx: 0, dy: 0 };
            const inFlight = waving.has(p.user_id);
            const initials = (p.display_name || p.username || "?")
              .split(" ")
              .map((w) => w[0])
              .filter(Boolean)
              .slice(0, 2)
              .join("")
              .toUpperCase();
            return (
              <div
                key={p.user_id}
                data-testid={`nearby-person-${p.user_id}`}
                style={{ transform: `translate(${off.dx}px, ${off.dy}px)` }}
                className="flex flex-col items-center gap-2"
              >
                <div className="relative">
                  <Avatar src={p.avatar_url} name={p.display_name} initials={initials} size={64} />
                  {p.mutual && (
                    <span
                      data-testid={`nearby-person-${p.user_id}-mutual`}
                      title="You both waved"
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-lime-500 border-[2.5px] border-ink flex items-center justify-center"
                    >
                      <MessageCircle size={12} strokeWidth={3} className="text-ink" />
                    </span>
                  )}
                </div>
                {!p.mutual ? (
                  <button
                    type="button"
                    onClick={() => onWave(p)}
                    disabled={p.i_waved || inFlight}
                    data-testid={`nearby-wave-${p.user_id}`}
                    aria-label={`Wave at ${p.display_name || p.username || "person"}`}
                    className={
                      "inline-flex items-center gap-1.5 border-[2.5px] border-ink rounded-full px-3 py-1.5 text-[10px] uppercase tracking-widest font-extrabold transition-colors " +
                      (p.i_waved
                        ? "bg-ink text-paper"
                        : "bg-paper text-ink hover:bg-lime")
                    }
                  >
                    {inFlight ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : (
                      <Hand size={11} strokeWidth={2.8} />
                    )}
                    {p.i_waved ? "Waved" : "Wave"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onWave(p)}
                    data-testid={`nearby-sayhi-${p.user_id}`}
                    className="inline-flex items-center gap-1.5 border-[2.5px] border-ink rounded-full px-3 py-1.5 text-[10px] uppercase tracking-widest font-extrabold bg-lime text-ink hover:bg-ink hover:text-paper transition-colors"
                  >
                    <MessageCircle size={11} strokeWidth={2.8} /> Say hi
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={onTurnOff}
        data-testid="nearby-turn-off"
        className="mt-5 text-[11px] uppercase tracking-widest text-ink/55 hover:text-ink transition-colors"
      >
        Turn Nearby off
      </button>
    </>
  );
}
